// Clients tab — client + lead lists, add/edit/convert flows.
import { useState, useEffect, useMemo } from "react";
import { doc, setDoc, deleteDoc, getDocs } from "firebase/firestore";
import { db, userCol } from "../lib/firebase";
import { packageDisplayState, rolloverPackageHistory, clientToday } from "../domain";
import { fmt, uid, pkgId, sl, cl, getCurrencySymbol } from "../lib/format";
import { pkgDisplayName, usePackageStates } from "../lib/packages";
import { themeTokens } from "../lib/theme";
import { Modal, Btn, Inp, Sel, SD, Empty, Icon, ConfirmButton, useToast } from "../ui/primitives";
import { PhoneField, PkgBuilder } from "../ui/forms";
import { CC, COUNTRIES } from "../data/countries";
import { CLIENT_PROFESSIONS, PROFESSIONS, SECTORS } from "../data/lists";
import { ClientDetailModal, ClientFormModal } from "../modals/client";
import { RenewPackageModal, ExtendPackageModal } from "../modals/package";

export const EC={name:"",mobile:"",email:"",dob:"",country:"",city:"",joiningDate:"",profession:"",sector:"",servicesAvailed:[],deliveryType:"Studio",package:{type:"none",totalAmount:0},packageHistory:[],currentPackageId:null,schedule:[],paid:0,classesAttended:0,packagePrice:0};
export const EL={name:"",mobile:"",email:"",employmentType:"",country:"",city:"",profession:"",sector:"",interest:"",source:"Referral",status:"New",followUp:"",notes:""};

export const Clients=({clients,setClients,applyClientAndPayment,profile,theme,userId,registerBack})=>{
  const toast=useToast();
  const currency=getCurrencySymbol(profile);
  const [tab,setTab]=useState("clients");
  const [modal,setModal]=useState(null);
  const [form,setForm]=useState(EC);
  const [formErrors,setFormErrors]=useState({});
  const [leads,setLeadsRaw]=useState([]);
  useEffect(()=>{
    if(!userId)return;
    getDocs(userCol(userId,"leads")).then(s=>setLeadsRaw(s.docs.map(d=>({id:d.id,...d.data()}))));
  },[userId]);
  const [lModal,setLModal]=useState(false);
  const [lForm,setLForm]=useState(EL);
  const [lFormErrors,setLFormErrors]=useState({});
  const [convModal,setConvModal]=useState(null);
  const [search,setSearch]=useState("");
  const [detailClientId,setDetailClientId]=useState(null);
  const detailClient=detailClientId?clients.find(c=>c.id===detailClientId)||null:null;
  useEffect(()=>{
    if(!registerBack)return;
    if(detailClientId)registerBack("clients-detail",()=>setDetailClientId(null));
    else registerBack("clients-detail",null);
  },[detailClientId,registerBack]);
  const [renewClient,setRenewClient]=useState(null);
  const [extendClient,setExtendClient]=useState(null);

  const psOf=usePackageStates(clients);

  const [sortBy,setSortBy]=useState("name"); // name|due|package
  const [filterBy,setFilterBy]=useState("all"); // all|paid|due|custom
  const [viewMode,setViewMode]=useState("card");
  const [showFilters,setShowFilters]=useState(false);
  const {isDark,cb,bc,tc,mc}=themeTokens(theme);

  const setLeads=async(l)=>{
    setLeadsRaw(l);
    if(userId){
      l.forEach(lead=>{
        const{id,...data}=lead;
        setDoc(doc(db,"users",userId,"leads",id),data).catch(e=>{
          console.error("Lead save failed:",e);
          toast("Couldn't save lead — check your connection and try again.","error");
        });
      });
      // Delete removed leads
      leads.forEach(lead=>{
        if(!l.find(x=>x.id===lead.id)){
          deleteDoc(doc(db,"users",userId,"leads",lead.id)).catch(e=>console.error("Lead delete failed:",e));
        }
      });
    }
  };
  const services=profile?.services?.length>0?profile.services:[];
  const defSvc=services.length===1?services[0]:"";

  const saveClient=()=>{
    const errs={};
    if(!form.name.trim())errs.name="Name is required";
    if(form.mobile&&!/^[\d\s\+\-\(\)]+$/.test(form.mobile))errs.mobile="Enter a valid mobile number";
    else if(form.mobile&&form.mobile.replace(/^\+\d{1,4}\s*/,"").replace(/\D/g,"").length<5)errs.mobile="Mobile number is too short";
    if(form.email&&!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))errs.email="Enter a valid email address";
    if(form.package?.type&&form.package.type!=="none"&&!form.package.startDate)errs.packageStart="Package start date is required";
    const dupMobile=form.mobile&&clients.find(c=>c.mobile&&c.mobile===form.mobile&&c.id!==form.id);
    if(dupMobile)errs.mobile=`Already used by ${dupMobile.name}`;
    if(Object.keys(errs).length){setFormErrors(errs);return;}
    setFormErrors({});
    const total=(Number(form.package&&form.package.customClasses)||0)||(Number(form.package&&form.package.templateSessions)||0);
    let obj={...form,packagePrice:form.package?.totalAmount||0};
    if(total>0)obj.classesRemaining=Math.max(0,total-(Number(form.classesAttended)||0));
    // If package set and no packageHistory yet — initialise it
    if(obj.package?.type&&obj.package.type!=="none"&&!obj.packageHistory?.length){
      const newPkgId=pkgId();
      obj={...obj,currentPackageId:newPkgId,packageHistory:[{packageId:newPkgId,...obj.package,status:"active",totalAmount:obj.packagePrice}]};
    }
    // Keep active packageHistory entry in sync with c.package when editing
    if(form.id&&obj.packageHistory?.length&&obj.currentPackageId){
      obj={...obj,packageHistory:obj.packageHistory.map(p=>
        p.packageId===obj.currentPackageId
          ?{...p,...obj.package,totalAmount:obj.packagePrice}
          :p
      )};
    }
    if(form.id)setClients(prev=>prev.map(c=>c.id===form.id?obj:c));
    else setClients(prev=>[...prev,{...obj,id:uid(),classesAttended:0,paid:0}]);
    setModal(null);setForm(EC);
  };

  const markInactive=(client)=>{
    // Block if package is still active
    const ps=packageDisplayState(client);
    if(ps.active&&!ps.isExpired){
      toast(`${client.name} has an active package. Complete or expire the package before moving to leads.`,"warning",4000);
      return;
    }
    // Remove from clients
    setClients(prev=>prev.filter(c=>c.id!==client.id));
    // Add to leads preserving full history
    const newLead={
      id:uid(),
      name:client.name,
      mobile:client.mobile||"",
      email:client.email||"",
      country:client.country||"",
      city:client.city||"",
      profession:client.profession||"",
      sector:client.sector||"",
      interest:client.serviceAvailed||"",
      source:"Existing Client",
      status:"Follow-up",
      followUp:"",
      notes:`Previously active client. Package: ${client.package?.type||"none"}. Sessions attended: ${client.classesAttended||0}.`,
      // Preserve enough to rebuild the client if they come back (doConvert reuses
      // previousClientId as the doc id, so payments/attendance/notes reconnect).
      // Legacy clients (no packageHistory) get their c.package snapshotted so it
      // still shows in the History tab after reconversion.
      packageHistory:client.packageHistory?.length
        ?client.packageHistory
        :(client.package?.type&&client.package.type!=="none"
            ?[{packageId:"legacy",...client.package,status:"expired",totalAmount:(Number(client.package.totalAmount)||0)||(Number(client.packagePrice)||0)}]
            :[]),
      currentPackageId:client.currentPackageId||null,
      classesAttended:client.classesAttended||0,
      classesRemaining:client.classesRemaining||0,
      joiningDate:client.joiningDate||"",
      previousClientId:client.id,
    };
    setLeads([...leads,newLead]);
    setTab("leads");
  };

  const saveLead=()=>{
    const errs={};
    if(!lForm.name.trim())errs.name="Name is required";
    if(lForm.mobile&&!/^[\d\s\+\-\(\)]+$/.test(lForm.mobile))errs.mobile="Enter a valid mobile number";
    else if(lForm.mobile&&lForm.mobile.replace(/^\+\d{1,4}\s*/,"").replace(/\D/g,"").length<5)errs.mobile="Mobile number is too short";
    if(lForm.email&&!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lForm.email))errs.email="Enter a valid email";
    if(Object.keys(errs).length){setLFormErrors(errs);return;}
    setLFormErrors({});
    if(lForm.id)setLeads(leads.map(l=>l.id===lForm.id?lForm:l));
    else setLeads([...leads,{...lForm,id:uid()}]);
    setLModal(false);setLForm(EL);
  };

  const doConvert=()=>{
    if(!convModal?.name)return;
    const {_lid,previousClientId,...seed}=convModal;
    const pkg=seed.package||{type:"none",totalAmount:0};
    const hasPkg=pkg.type&&pkg.type!=="none";
    const total=(Number(pkg.customClasses)||0)||(Number(pkg.templateSessions)||0);
    // Returning client: reuse the old doc id so their never-deleted payments /
    // attendance / notes reconnect, and roll the carried packageHistory forward
    // like a renewal — clean-slate counters for the new package, lifetime record
    // stays in packageHistory + the reconnected collections.
    const id=previousClientId||uid();
    const hist=hasPkg
      ? rolloverPackageHistory({packageHistory:seed.packageHistory,currentPackageId:seed.currentPackageId},pkg,pkgId(),clientToday())
      : {packageHistory:seed.packageHistory||[],currentPackageId:null};
    const client={
      ...seed,
      id,
      joiningDate:seed.joiningDate||clientToday(),
      classesAttended:0,
      classesRemaining:total>0?total:0,
      paid:0,
      packagePrice:hasPkg?(Number(pkg.totalAmount)||0):0,
      package:hasPkg?{...pkg,extendedDays:0}:pkg,
      ...hist,
      previousClientId:previousClientId||null,
    };
    setClients(prev=>prev.some(c=>c.id===id)?prev.map(c=>c.id===id?client:c):[...prev,client]);
    setLeads(leads.filter(l=>l.id!==_lid));
    setConvModal(null);
  };

  // Filter + sort — recomputed only when its own inputs change, not on every
  // render (opening a modal, toggling the filter panel, an unrelated toast).
  const filtered=useMemo(()=>clients
    .filter(c=>{
      const matchSearch=c.name.toLowerCase().includes(search.toLowerCase())||c.mobile?.includes(search);
      if(!matchSearch)return false;
      if(filterBy==="due")return (c.packagePrice-(c.paid||0))>0;
      if(filterBy==="paid")return (c.packagePrice-(c.paid||0))<=0;
      if(filterBy==="custom")return c.package?.type==="custom";
      if(filterBy==="expiring")return c.classesRemaining===1;
      return true;
    })
    .sort((a,b)=>{
      if(sortBy==="name")return a.name.localeCompare(b.name);
      if(sortBy==="due")return ((b.packagePrice||0)-(b.paid||0))-((a.packagePrice||0)-(a.paid||0));
      if(sortBy==="joined")return (b.joiningDate||"").localeCompare(a.joiningDate||"");
      return 0;
    }),[clients,search,filterBy,sortBy]);
  const activeLeads=leads.filter(l=>l.status!=="Converted");
  const convertedLeads=leads.filter(l=>l.status==="Converted");

  return(
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-2 p-1 rounded-xl w-fit" style={{background:isDark?"#1e293b":"#f3f4f6"}}>
          {["clients","leads"].map(t=>(
            <button key={t} onClick={()=>setTab(t)}
              className="px-4 py-1.5 rounded-lg text-sm font-semibold transition-all capitalize"
              style={{background:tab===t?(isDark?"#0f172a":"#fff"):"transparent",color:tab===t?(theme?.primary||"#0f766e"):mc,boxShadow:tab===t?"0 1px 3px rgba(0,0,0,0.1)":"none"}}>
              {t==="leads"?"Leads / CRM":`Active ${cl(profile,2)}`}
            </button>
          ))}
        </div>
        {tab==="clients"&&(
          <Btn onClick={()=>{setForm({...EC,serviceAvailed:defSvc});setModal("client");}} theme={theme} className="flex-shrink-0 px-3" title={`Add ${cl(profile)}`} aria-label={`Add ${cl(profile)}`}><Icon name="plus" size={16}/></Btn>
        )}
      </div>

      {tab==="clients"&&(
        <>
          {/* Search + filter toolbar */}
          <div className="space-y-2">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Icon name="search" size={16} className="absolute left-3 top-3" style={{color:mc}}/>
                <input className="w-full pl-9 pr-4 py-2.5 border rounded-xl text-sm focus:outline-none"
                  style={{background:isDark?"#1e293b":"#f9fafb",borderColor:isDark?"#334155":"#e5e7eb",color:tc}}
                  placeholder={`Search ${cl(profile,2).toLowerCase()}...`} value={search} onChange={e=>setSearch(e.target.value)}/>
              </div>
              {/* Filter button */}
              <button onClick={()=>setShowFilters(s=>!s)}
                className="p-2.5 rounded-xl border transition-all"
                style={{background:showFilters||filterBy!=="all"?(isDark?"#0f172a":theme?.light):"transparent",color:filterBy!=="all"?theme?.primary:mc,borderColor:filterBy!=="all"?theme?.primary:(isDark?"#334155":"#e5e7eb")}}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
              </button>
              {/* View toggle icons */}
              <div className="flex rounded-xl border overflow-hidden" style={{borderColor:isDark?"#334155":"#e5e7eb"}}>
                {[{v:"card",icon:<><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></>},{v:"list",icon:<><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></>},{v:"tile",icon:<><rect x="3" y="3" width="18" height="4" rx="1"/><rect x="3" y="10" width="18" height="4" rx="1"/><rect x="3" y="17" width="18" height="4" rx="1"/></>}].map(({v,icon})=>(
                  <button key={v} onClick={()=>setViewMode(v)}
                    className="p-2 transition-all"
                    style={{background:viewMode===v?(theme?.primary||"#0f766e"):"transparent",color:viewMode===v?"#fff":mc}}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">{icon}</svg>
                  </button>
                ))}
              </div>
            </div>

            {/* Filter + sort panel */}
            {showFilters&&(
              <div className="rounded-xl p-3 border space-y-3" style={{background:isDark?"#1e293b":"#f8fafc",borderColor:isDark?"#334155":"#e5e7eb"}}>
                <div>
                  <p className="text-xs font-semibold mb-2" style={{color:mc}}>Filter</p>
                  <div className="flex flex-wrap gap-1.5">
                    {[{id:"all",label:"All"},{id:"due",label:"Has Dues"},{id:"paid",label:"Fully Paid"},{id:"custom",label:"Custom Pkg"},{id:"expiring",label:"Expiring"}].map(f=>(
                      <button key={f.id} onClick={()=>setFilterBy(f.id)}
                        className="px-3 py-1 rounded-full text-xs font-semibold border transition-all"
                        style={{background:filterBy===f.id?(theme?.primary||"#0f766e"):"transparent",color:filterBy===f.id?"#fff":mc,borderColor:filterBy===f.id?(theme?.primary||"#0f766e"):(isDark?"#334155":"#e5e7eb")}}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold mb-2" style={{color:mc}}>Sort by</p>
                  <div className="flex gap-1.5">
                    {[{id:"name",label:"Name"},{id:"due",label:"Dues"},{id:"joined",label:"Joined"}].map(s=>(
                      <button key={s.id} onClick={()=>setSortBy(s.id)}
                        className="px-3 py-1 rounded-full text-xs font-semibold border transition-all"
                        style={{background:sortBy===s.id?(theme?.primary||"#0f766e"):"transparent",color:sortBy===s.id?"#fff":mc,borderColor:sortBy===s.id?(theme?.primary||"#0f766e"):(isDark?"#334155":"#e5e7eb")}}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {filtered.length===0
            ?<Empty icon="clients" message={`No ${cl(profile,2).toLowerCase()} found.`} cta={`Add ${cl(profile)}`} onCta={()=>{setForm({...EC,serviceAvailed:defSvc});setModal("client");}} theme={theme}/>
            :<div className={viewMode==="tile"?"grid grid-cols-2 gap-2":""}>
            {filtered.map(c=>{
              const ps=psOf(c);
              const due=ps.balance;
              const pkg=c.package;
              const expiry=ps.expiryDate;
              const isExpired=ps.isExpired;

              // TILE view
              if(viewMode==="tile")return(
                <div key={c.id} className="rounded-2xl p-3 border mb-0 cursor-pointer"
                  style={{background:cb,borderColor:bc}}
                  onClick={()=>setDetailClientId(c.id)}>
                  <p className="font-black text-sm truncate" style={{color:tc}}>{c.name}</p>
                  {c.mobile&&<p className="flex items-center gap-1 text-xs mt-0.5 truncate" style={{color:theme?.primary}}><Icon name="phone" size={10}/>{c.mobile}</p>}
                  <div className="mt-2 px-2 py-1 rounded-lg text-center" style={{background:due>0?"#fef2f2":due<0?"#dbeafe":"#dcfce7"}}>
                    <span className="text-xs font-bold" style={{fontVariantNumeric:"tabular-nums",color:due>0?"#dc2626":due<0?"#1d4ed8":"#16a34a"}}>
                      {due>0?`${fmt(due,currency)} due`:due<0?`+${fmt(Math.abs(due),currency)}`:"Paid ✓"}
                    </span>
                  </div>
                </div>
              );

              // LIST view — ledger-style line item: dotted divider, tabular amount
              if(viewMode==="list")return(
                <div key={c.id} className="flex items-center gap-3 px-4 py-3"
                  style={{borderTop:"1px dotted "+bc}}>
                  <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 font-black text-sm" style={{background:theme?.light,color:theme?.primary}}>
                    {c.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate" style={{color:tc}}>{c.name}</p>
                    <p className="text-xs truncate" style={{color:mc}}>{c.mobile} · {c.serviceAvailed||"—"}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-bold" style={{color:due>0?"#dc2626":"#16a34a",fontVariantNumeric:"tabular-nums"}}>{due>0?fmt(due,currency):"Paid"}</p>
                    {due>0&&<p className="text-[10px] uppercase tracking-wide" style={{color:mc}}>due</p>}
                  </div>
                  <button onClick={()=>setDetailClientId(c.id)} className="p-1.5 rounded-lg ml-1" style={{color:theme?.primary}}><Icon name="eye" size={14}/></button>
                  <button onClick={()=>{setForm({...c});setModal("client");}} className="p-1.5 rounded-lg" style={{color:mc}}><Icon name="edit" size={14}/></button>
                </div>
              );

              // CARD view (default)
              return(
              <div key={c.id} className="rounded-2xl p-4 shadow-sm border mb-3 select-none"
                style={{background:cb,borderColor:bc}}>
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-black text-base leading-tight" style={{color:tc}}>{c.name}</p>
                    {c.mobile&&<p className="flex items-center gap-1.5 text-sm font-semibold mt-0.5" style={{color:theme?.primary}}><Icon name="phone" size={12}/>{c.mobile}</p>}
                  </div>
                  <div className="flex gap-1 ml-2 flex-shrink-0">
                    <button onClick={()=>setDetailClientId(c.id)} className="p-2 rounded-xl" title="View Details" style={{color:theme?.primary,background:theme?.light}}><Icon name="eye" size={15}/></button>
                    <button onClick={()=>{setForm({...c});setModal("client");}} className="p-2 rounded-xl" title="Edit" style={{color:mc,background:isDark?"#334155":"#f3f4f6"}}><Icon name="edit" size={15}/></button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {c.serviceAvailed&&<span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{background:theme?.light,color:theme?.primary}}>{c.serviceAvailed}</span>}
                  {c.deliveryType&&<span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{background:isDark?"#334155":"#f1f5f9",color:mc}}>{c.deliveryType}</span>}
                </div>
                <div className="rounded-xl p-2.5 mb-2 space-y-1" style={{background:isDark?"#0f172a":"#f8fafc"}}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold" style={{color:mc}}>
                      {pkgDisplayName(pkg)}
                    </span>
                    {pkg?.totalAmount>0&&<span className="text-xs font-bold" style={{fontVariantNumeric:"tabular-nums",color:tc}}>{fmt(pkg.totalAmount,currency)}</span>}
                  </div>
                  {(pkg?.type==="custom"||pkg?.type==="template_sessions")&&(
                    <div className="flex items-center gap-3 text-xs" style={{color:mc}}>
                      <span className="flex items-center gap-1"><Icon name="check" size={11}/>{c.classesAttended||0} attended</span>
                      {ps.isSessionBased&&(ps.classesRemaining||0)===0&&(
                        <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full" style={{background:"#fef3c7",color:"#d97706"}}><Icon name="convert" size={10}/>Renew needed</span>
                      )}
                      <span className="flex items-center gap-1" style={{fontVariantNumeric:"tabular-nums"}}><Icon name="appointments" size={11}/>{c.classesRemaining??0} remaining</span>
                    </div>
                  )}
                  {expiry&&<p className="flex items-center gap-1 text-xs font-semibold" style={{color:isExpired?"#dc2626":"#16a34a"}}><Icon name={isExpired?"alert":"calendar"} size={11}/>{isExpired?"Expired:":"Valid till:"} {expiry}</p>}
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-xl" style={{background:due>0?"#fef2f2":due<0?"#dbeafe":"#dcfce7"}}>
                  <span className="flex items-center gap-1 text-xs font-bold" style={{fontVariantNumeric:"tabular-nums",color:due>0?"#dc2626":due<0?"#1d4ed8":"#16a34a"}}>
                    <Icon name={due>0?"payments":"check"} size={12}/>
                    {due>0?`Due: ${fmt(due,currency)}`:due<0?`Advance: ${fmt(Math.abs(due),currency)}`:"Fully Paid"}
                  </span>
                  {c.paid>0&&<span className="text-xs" style={{fontVariantNumeric:"tabular-nums",color:mc}}>Paid {fmt(c.paid,currency)}</span>}
                </div>
                {c.classesRemaining===1&&(
                  <div className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{background:"#fffbeb"}}>
                    <Icon name="alert" size={12}/><p className="text-xs text-amber-700 font-semibold">1 {sl(profile,1).toLowerCase()} remaining — time to renew!</p>
                  </div>
                )}
              </div>
              );
            })}
            </div>
          }
        </>
      )}

      {tab==="leads"&&(
        <div className="space-y-3">
          <div className="flex justify-end">
            <Btn variant="gold" onClick={()=>{setLForm({...EL});setLFormErrors({});setLModal(true);}} theme={theme}><Icon name="plus" size={16}/>Add Lead</Btn>
          </div>
          {activeLeads.length===0
            ?<Empty icon="user" message="No leads yet." cta="Add Lead" onCta={()=>{setLForm({...EL});setLModal(true);}} theme={theme}/>
            :activeLeads.map(l=>(
              <div key={l.id} className="rounded-2xl p-4 shadow-sm border" style={{background:cb,borderColor:bc}}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-bold" style={{color:tc}}>{l.name}</p>
                    <p className="text-xs mt-0.5" style={{color:mc}}>{l.mobile}{l.email?` · ${l.email}`:""}</p>
                    <p className="text-xs" style={{color:mc}}>{l.city?`${l.city}, `:""}{l.country}</p>
                    {l.interest&&<p className="text-xs font-semibold mt-0.5" style={{color:theme?.primary}}>{l.interest} · {l.source}</p>}
                    {l.notes&&<p className="text-xs italic mt-1" style={{color:mc}}>{l.notes}</p>}
                    {l.classesAttended>0&&(
                      <p className="flex items-center gap-1 text-xs mt-1 font-medium" style={{fontVariantNumeric:"tabular-nums",color:mc}}>
                        <Icon name="check" size={11}/>{l.classesAttended} {sl(profile,l.classesAttended===1?1:2).toLowerCase()} attended
                        {l.packageHistory?.length>0&&` · ${l.packageHistory.length} package${l.packageHistory.length>1?"s":""}`}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="text-xs px-2 py-1 rounded-full font-semibold"
                      style={{background:l.status==="New"?"#dbeafe":l.status==="Follow-up"?"#fffbeb":l.status==="Converted"?"#dcfce7":"#f3f4f6",color:l.status==="New"?"#1d4ed8":l.status==="Follow-up"?"#d97706":l.status==="Converted"?"#16a34a":"#6b7280"}}>
                      {l.status}
                    </span>
                    <div className="flex gap-1">
                      <button onClick={()=>{setLForm(l);setLModal(true);}} className="p-1.5 rounded-lg" style={{color:theme?.primary}}><Icon name="edit" size={14}/></button>
                      <ConfirmButton label={<Icon name="trash" size={14}/>} confirmLabel="Delete?" onConfirm={()=>setLeads(leads.filter(x=>x.id!==l.id))} danger className="p-1.5 rounded-lg" style={{color:"#ef4444"}}/>
                    </div>
                  </div>
                </div>
                {l.followUp&&<p className="text-xs mt-2 px-2 py-1 rounded-lg inline-block" style={{background:theme?.light,color:theme?.primary}}>Follow-up: {l.followUp}</p>}
              </div>
            ))
          }
          {convertedLeads.length>0&&(
            <div className="fixed bottom-24 left-4 md:bottom-6 md:left-72 z-30">
              <button onClick={()=>{const l=convertedLeads[0];setConvModal({...EC,name:l.name,mobile:l.mobile,email:l.email||"",country:l.country||"",city:l.city||"",profession:l.profession||"",sector:l.sector||"",serviceAvailed:l.interest||defSvc,previousClientId:l.previousClientId||null,packageHistory:l.packageHistory||[],currentPackageId:l.currentPackageId||null,joiningDate:l.joiningDate||"",_lid:l.id});}}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl shadow-lg text-sm font-bold"
                style={{background:theme?.primary,color:"#fff"}}>
                <Icon name="convert" size={16}/>
                {convertedLeads.length} Converted Lead{convertedLeads.length>1?"s":""}
              </button>
            </div>
          )}
        </div>
      )}

      {detailClient&&<ClientDetailModal c={detailClient} onClose={()=>setDetailClientId(null)} theme={theme} currency={currency} userId={userId} profile={profile} onRenew={()=>{setRenewClient(detailClient);setDetailClientId(null);}} onExtend={()=>{setExtendClient(detailClient);setDetailClientId(null);}}/> }
      {/* Package Renewal Modal */}
      {renewClient&&(
        <RenewPackageModal
          client={renewClient}
          onClose={()=>setRenewClient(null)}
          onRenew={({apply,payment})=>{setRenewClient(null);applyClientAndPayment(renewClient.id,apply,payment,toast);}}
          theme={theme}
          currency={currency}
          profile={profile}
        />
      )}

      {/* Package Extension Modal */}
      {extendClient&&(
        <ExtendPackageModal
          client={extendClient}
          onClose={()=>setExtendClient(null)}
          onExtend={({apply,payment})=>{setExtendClient(null);applyClientAndPayment(extendClient.id,apply,payment,toast);}}
          theme={theme}
          currency={currency}
        />
      )}

      {modal==="client"&&(
        <ClientFormModal
          f={form} setF={setForm}
          onSave={saveClient}
          errors={formErrors}
          onCancel={()=>{setModal(null);setForm(EC);setFormErrors({});}}
          onMarkInactive={form.id?()=>{markInactive(form);setModal(null);setForm(EC);}:null}
          title={form.id?`Edit ${cl(profile)}`:`Add New ${cl(profile)}`}
          clients={clients} services={services} defSvc={defSvc} mc={mc} theme={theme}
          currency={currency} profile={profile}
        />
      )}

      {lModal&&(
        <Modal title={lForm.id?"Edit Lead":"Add Lead"} onClose={()=>{setLModal(false);setLForm(EL);setLFormErrors({});}} theme={theme}>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Inp label="Name *" value={lForm.name} onChange={e=>{setLForm(p=>({...p,name:e.target.value}));setLFormErrors(p=>({...p,name:""}));}} theme={theme} error={lFormErrors.name}/></div>
            <div className="col-span-2"><PhoneField label="Mobile" value={lForm.mobile||""} onChange={v=>{setLForm(p=>({...p,mobile:v}));setLFormErrors(p=>({...p,mobile:""}));}} onCountryChange={c=>setLForm(p=>({...p,country:c,city:""}))} error={lFormErrors.mobile} theme={theme}/></div>
            <div className="col-span-2"><Inp label="Email" type="email" value={lForm.email||""} onChange={e=>setLForm(p=>({...p,email:e.target.value}))} theme={theme}/></div>
            <Sel label="Employment Type" value={lForm.employmentType||""} onChange={e=>setLForm(p=>({...p,employmentType:e.target.value}))} theme={theme}>
              <option value="">-- Select --</option>
              <option value="Job">Job (Employed)</option>
              <option value="Business">Business Owner</option>
              <option value="Freelance">Freelance</option>
              <option value="Student">Student</option>
              <option value="Other">Other</option>
            </Sel>
            <SD label="Country" value={lForm.country} onChange={v=>setLForm(p=>({...p,country:v,city:""}))} options={COUNTRIES} theme={theme}/>
            <SD label="City" value={lForm.city} onChange={v=>setLForm(p=>({...p,city:v}))} options={lForm.country?(CC[lForm.country]||[]):[]} placeholder={lForm.country?"Select city":"Country first"} theme={theme}/>
            <SD label="Profession" value={lForm.profession} onChange={v=>setLForm(p=>({...p,profession:v}))} options={CLIENT_PROFESSIONS} theme={theme}/>
            <SD label="Sector" value={lForm.sector} onChange={v=>setLForm(p=>({...p,sector:v}))} options={SECTORS} theme={theme}/>
            <div className="col-span-2">
              {services.length>0
                ?<SD label="Interest / Service" value={lForm.interest} onChange={v=>setLForm(p=>({...p,interest:v}))} options={services} theme={theme}/>
                :<Inp label="Interest / Service" value={lForm.interest} onChange={e=>setLForm(p=>({...p,interest:e.target.value}))} theme={theme}/>
              }
            </div>
            <Sel label="Source" value={lForm.source} onChange={e=>setLForm(p=>({...p,source:e.target.value}))} theme={theme}>
              {["Referral","Instagram","Walk-in","WhatsApp","Website","Other"].map(s=><option key={s}>{s}</option>)}
            </Sel>
            <Sel label="Status" value={lForm.status} onChange={e=>setLForm(p=>({...p,status:e.target.value}))} theme={theme}>
              {["New","Follow-up","Converted","Lost"].map(s=><option key={s}>{s}</option>)}
            </Sel>
            <div className="col-span-2"><Inp label="Follow-up Date" type="date" value={lForm.followUp} onChange={e=>setLForm(p=>({...p,followUp:e.target.value}))} theme={theme}/></div>
            <div className="col-span-2 flex flex-col gap-1">
              <label className="text-xs font-semibold uppercase tracking-wider" style={{color:isDark?"#94a3b8":"#6b7280"}}>Notes</label>
              <textarea className="border rounded-xl px-4 py-2.5 text-sm focus:outline-none resize-none"
                style={{background:isDark?"#0f172a":"#f9fafb",borderColor:isDark?"#334155":"#e5e7eb",color:tc}}
                rows={3} value={lForm.notes} onChange={e=>setLForm(p=>({...p,notes:e.target.value}))}/>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Btn variant="secondary" onClick={()=>{setLModal(false);setLForm(EL);}} theme={theme}>Cancel</Btn>
            <Btn variant="gold" onClick={saveLead} theme={theme}><Icon name="check" size={16}/>{lForm.id?"Update":"Save Lead"}</Btn>
          </div>
        </Modal>
      )}

      {convModal&&(
        <Modal title="Convert Lead → Client" onClose={()=>setConvModal(null)} theme={theme}>
          <p className="text-sm mb-4" style={{color:mc}}>Pre-filled from lead. Complete remaining details.</p>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Inp label="Full Name *" value={convModal.name} onChange={e=>setConvModal(p=>({...p,name:e.target.value}))} theme={theme}/></div>
            <Inp label="Mobile" value={convModal.mobile} onChange={e=>setConvModal(p=>({...p,mobile:e.target.value}))} theme={theme}/>
            <Inp label="Email" value={convModal.email} onChange={e=>setConvModal(p=>({...p,email:e.target.value}))} theme={theme}/>
            <Inp label="Date of Birth" type="date" value={convModal.dob||""} onChange={e=>setConvModal(p=>({...p,dob:e.target.value}))} theme={theme}/>
            <SD label="Profession" value={convModal.profession} onChange={v=>setConvModal(p=>({...p,profession:v}))} options={PROFESSIONS} theme={theme}/>
            <SD label="Country" value={convModal.country} onChange={v=>setConvModal(p=>({...p,country:v,city:""}))} options={COUNTRIES} theme={theme}/>
            <SD label="City" value={convModal.city} onChange={v=>setConvModal(p=>({...p,city:v}))} options={convModal.country?(CC[convModal.country]||[]):[]} theme={theme}/>
            <div className="col-span-2">
              <SD label="Type of Delivery" value={convModal.deliveryType||"Studio"} onChange={v=>setConvModal(p=>({...p,deliveryType:v}))} options={["Home Visit","Studio","Online"]} theme={theme}/>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1" style={{color:mc}}>Package</label>
              <PkgBuilder pkg={convModal.package||{type:"monthly",totalAmount:0}} onChange={pkg=>setConvModal(p=>({...p,package:pkg}))} theme={theme} currency={currency} profile={profile}/>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Btn variant="secondary" onClick={()=>setConvModal(null)} theme={theme}>Cancel</Btn>
            <Btn onClick={doConvert} theme={theme}><Icon name="check" size={16}/>Save as Client</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
};
